import React from 'react';
import { Message } from '../../types';
import { Avatar } from '../ui/Avatar';
import { MessageSquare, Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import { cn, safeFormat } from '../../lib/utils';
import { Input } from '../ui/Input';

interface MessageItemProps {
  messages: Message[];
  isOwn: boolean;
  onReply?: (message: Message) => void;
  onEdit?: (id: string, body: string) => void;
  onDelete?: (id: string) => void;
  allMessages: Message[];
}

export const MessageItem: React.FC<MessageItemProps> = ({ 
  messages, 
  isOwn, 
  onReply,
  onEdit,
  onDelete,
  allMessages 
}) => {
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editValue, setEditValue] = React.useState('');
  const firstMsg = messages[0];

  const handleEditSubmit = (msgId: string) => {
    if (editValue.trim() && onEdit) {
      onEdit(msgId, editValue.trim());
    }
    setEditingId(null);
  };

  return (
    <div className={cn(
      "flex gap-3 animate-in fade-in slide-in-from-bottom-2 duration-300",
      isOwn ? "flex-row-reverse" : "flex-row"
    )}>
      {!isOwn && (
        <Avatar 
          userId={firstMsg.senderUserId || 'unknown'} 
          initials={(firstMsg.senderName || '').substring(0, 2)} 
          size="sm" 
          className="mt-1"
        />
      )}
      <div className={cn(
        "flex flex-col max-w-[85%] sm:max-w-[75%] space-y-1",
        isOwn ? "items-end" : "items-start"
      )}>
        {!isOwn && (
          <div className="flex items-center gap-2 px-1">
            <span className="text-xs font-bold text-gray-900 dark:text-white">{firstMsg.senderName}</span>
            <span className="text-[10px] text-gray-400">{safeFormat(firstMsg.createdAt, 'h:mm a')}</span>
          </div>
        )}
        {isOwn && (
          <span className="text-[10px] text-gray-400 px-1">{safeFormat(firstMsg.createdAt, 'h:mm a')}</span>
        )}
        
        <div className="space-y-1 flex flex-col items-inherit">
          {messages.map((msg) => {
            const repliedMsg = msg.replyToId ? allMessages.find(m => m.id === msg.replyToId) : null;
            return (
              <div 
                key={msg.id}
                className="group relative"
              >
                {repliedMsg && (
                  <div className={cn(
                    "text-[10px] text-gray-400 mb-1 flex items-center gap-1",
                    isOwn ? "justify-end" : "justify-start"
                  )}>
                    <div className="w-4 h-4 rounded-full bg-gray-100 flex items-center justify-center dark:bg-slate-800">
                      <MessageSquare size={8} />
                    </div>
                    <span>Replying to: {repliedMsg.body.substring(0, 30)}...</span>
                  </div>
                )}
                <div className={cn(
                  "px-4 py-2 text-sm transition-all shadow-sm",
                  isOwn 
                    ? "bg-navy-900 text-white rounded-tl-xl rounded-tr-xl rounded-bl-xl rounded-br-sm" 
                    : "bg-white border border-gray-200 text-gray-900 rounded-tl-xl rounded-tr-xl rounded-bl-sm rounded-br-xl dark:bg-slate-900 dark:text-white",
                  msg.isPending && "opacity-60",
                  editingId === msg.id && "w-full min-w-[200px]"
                )}>
                  {editingId === msg.id ? (
                    <div className="flex flex-col gap-2">
                      <Input
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="bg-navy-800 text-white border-navy-700 h-8 text-xs"
                        autoFocus
                      />
                      <div className="flex justify-end gap-2">
                        <button onClick={() => setEditingId(null)} className="p-1 hover:bg-navy-800 rounded">
                          <X size={12} />
                        </button>
                        <button onClick={() => handleEditSubmit(msg.id)} className="p-1 hover:bg-navy-800 rounded">
                          <Check size={12} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {msg.body}
                      {msg.isEdited && (
                        <span className="ml-2 text-[8px] opacity-60 uppercase tracking-tighter">(edited)</span>
                      )}
                    </>
                  )}
                </div>
                
                <div className={cn(
                  "absolute top-1/2 -translate-y-1/2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10",
                  isOwn ? "-left-24" : "-right-24"
                )}>
                  {onReply && (
                    <button
                      onClick={() => onReply(msg)}
                      className="p-1.5 bg-white border border-gray-200 rounded-full shadow-sm text-gray-400 hover:text-gray-600 dark:bg-slate-900"
                      title="Reply"
                    >
                      <Plus size={14} />
                    </button>
                  )}
                  {isOwn && onEdit && editingId !== msg.id && (
                    <button
                      onClick={() => {
                        setEditingId(msg.id);
                        setEditValue(msg.body);
                      }}
                      className="p-1.5 bg-white border border-gray-200 rounded-full shadow-sm text-gray-400 hover:text-gray-600 dark:bg-slate-900"
                      title="Edit"
                    >
                      <Pencil size={12} />
                    </button>
                  )}
                  {isOwn && onDelete && (
                    <button
                      onClick={() => onDelete(msg.id)}
                      className="p-1.5 bg-white border border-gray-200 rounded-full shadow-sm text-red-400 hover:text-red-600 dark:bg-slate-900"
                      title="Delete"
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
